import React from 'react';
import { useAudioAnalysis } from '../hooks/useApi';
import { formatDate, getSentimentBadgeClass, getCategoryBadgeClass, getBooleanBadgeText } from '../utils';
import LoadingSpinner from './LoadingSpinner';
import apiService from '../services/api';
import './AnalysisModal.css';

interface AnalysisModalProps {
  analysisId: number;
  onClose: () => void;
}

const AnalysisModal: React.FC<AnalysisModalProps> = ({ analysisId, onClose }) => {
  const { analysis, loading, error } = useAudioAnalysis(analysisId);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = async () => {
    if (!analysis || !analysis.downloadToken) {
      alert('Token de download não disponível');
      return;
    }

    try {
      const downloadUrl = apiService.getDownloadUrl(analysis.downloadToken);
      
      console.log('🔗 Iniciando download via fetch (modal):', downloadUrl);
      
      // Fazer requisição fetch para obter o arquivo
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro no download: ${response.status} - ${errorText}`);
      }
      
      // Verificar headers
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      console.log('📤 Headers de resposta (modal):', {
        contentLength,
        contentType,
        contentDisposition: response.headers.get('content-disposition')
      });
      
      // Obter o blob do arquivo
      const blob = await response.blob();
      
      console.log('📊 Arquivo baixado (modal):', {
        size: blob.size,
        type: blob.type,
        sizeInMB: (blob.size / 1024 / 1024).toFixed(2) + ' MB'
      });
      
      // Verificar se o arquivo não é muito pequeno
      if (blob.size < 1000) {
        console.warn('⚠️ Arquivo muito pequeno (modal):', blob.size, 'bytes');
        
        const text = await blob.text();
        console.error('📄 Conteúdo do blob (modal):', text.substring(0, 200));
        
        throw new Error(`Arquivo muito pequeno (${blob.size} bytes). Pode ser um erro no servidor.`);
      }
      
      // Criar URL do blob para download
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = analysis.file_name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL do blob
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('✅ Download concluído (modal):', analysis.file_name);
      
    } catch (error: any) {
      console.error('❌ Erro ao fazer download (modal):', error);
      alert(`Erro ao fazer download do arquivo: ${error.message || 'Erro desconhecido'}`);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Detalhes da Análise #{analysisId}</h2>
          <button onClick={onClose} className="modal-close">
            ✕
          </button>
        </div>
        
        <div className="modal-body">
          {loading && <LoadingSpinner message="Carregando análise..." />}
          
          {error && (
            <div className="error-message">
              <p>Erro ao carregar análise: {error}</p>
            </div>
          )}
          
          {analysis && (
            <div className="analysis-details">
              <div className="analysis-meta">
                <div className="meta-item">
                  <label>ID:</label>
                  <span>#{analysis.id}</span>
                </div>
                <div className="meta-item">
                  <label>Data:</label>
                  <span>{formatDate(analysis.created_at)}</span>
                </div>
                <div className="meta-item">
                  <label>Rádio:</label>
                  <span>{analysis.radio}</span>
                </div>
                <div className="meta-item">
                  <label>Cliente:</label>
                  <span>{analysis.client}</span>
                </div>
              </div>
              
              <div className="analysis-badges">
                <span className={`badge ${getSentimentBadgeClass(analysis.sentiment_type)}`}>
                  {analysis.sentiment_type}
                </span>
                {analysis.categoria && (
                  <span className={`badge ${getCategoryBadgeClass(analysis.categoria)}`}>
                    {analysis.categoria}
                  </span>
                )}
                {analysis.mentioned_prefecture && (
                  <span className="badge badge-prefecture">
                    🏛️ Prefeitura
                  </span>
                )}
              </div>
              
              <div className="analysis-content">
                <h3>Conteúdo da Análise</h3>
                <div className="content-text">
                  {analysis.content}
                </div>
              </div>
              
              {analysis.termo && (
                <div className="analysis-termo">
                  <h4>Termo:</h4>
                  <p>{analysis.termo}</p>
                </div>
              )}
              
              <div className="analysis-tech-info">
                <h4>Informações Técnicas</h4>
                <div className="tech-info-grid">
                  <div className="tech-item">
                    <label>Modelo IA:</label>
                    <span>{analysis.ai_model}</span>
                  </div>
                  <div className="tech-item">
                    <label>Arquivo:</label>
                    <span>{analysis.file_name}</span>
                  </div>
                  <div className="tech-item">
                    <label>Diretório:</label>
                    <span>{analysis.file_directory}</span>
                  </div>
                  <div className="tech-item">
                    <label>Email Enviado:</label>
                    <span>{getBooleanBadgeText(!!analysis.email_sent_at)}</span>
                  </div>
                  {analysis.email_sent_at && (
                    <div className="tech-item">
                      <label>Data do Email:</label>
                      <span>{formatDate(analysis.email_sent_at)}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {analysis.sentiment_type === 'negativo' && analysis.downloadToken && (
                <div className="analysis-actions">
                  <button onClick={handleDownload} className="download-button">
                    📥 Baixar Áudio
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisModal;
